import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MailCheck, ArrowLeft } from "lucide-react";
import useBrand, { BrandMark } from "@/hooks/useBrand";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const brand = useBrand();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
      toast.success("Instruksi reset password sudah dikirim (jika email terdaftar).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim link reset");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen brand-hero flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <BrandMark brand={brand} size={52} />
          <div>
            <div className="font-extrabold text-2xl tracking-tight text-slate-900">{brand.company_name || "Gogojalan"}</div>
            <div className="text-xs text-slate-500">Reset Password</div>
          </div>
        </div>

        <div className="paper p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-sent-panel">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e4ecc9]">
                <MailCheck size={28} className="text-[#556b2f]" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Cek email Anda</h1>
              <p className="text-sm text-slate-500">
                Kami sudah mengirim link reset password ke <b>{email}</b> jika email tersebut terdaftar.
                Link berlaku selama 15 menit.
              </p>
              <p className="text-xs text-slate-400">Tidak menerima email? Cek folder Spam atau minta ulang.</p>
              <div className="pt-2 flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSent(false)}
                  data-testid="forgot-resend-btn"
                  className="w-full"
                >
                  Kirim ulang
                </Button>
                <Link to="/login" className="text-sm text-[#556b2f] hover:underline inline-flex items-center justify-center gap-1">
                  <ArrowLeft size={14} /> Kembali ke login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Lupa password?</h1>
              <p className="text-sm text-slate-500 mb-6">
                Masukkan email admin yang terdaftar. Kami akan kirim link untuk membuat password baru.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@gogojalan.id"
                    required
                  />
                </div>
                <Button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#556b2f] hover:bg-[#3d4d1e] h-11 text-base"
                >
                  {busy ? "Mengirim..." : "Kirim link reset"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  data-testid="forgot-back-link"
                  className="text-sm text-[#556b2f] hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Kembali ke login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
